import type { DomSanitizer } from '@angular/platform-browser';

import type { BarSeriesOption } from 'echarts/charts';
import type { CallbackDataParams } from 'echarts/types/dist/shared';
import { pipe } from 'rxjs';

import {
  ReportDashboardComponentChartType,
  ReportDashboardComponentKind,
  ReportDashboardComponentViewMode,
  ReportsTranslations,
  DatasetBase,
  ReportDashboardComponentViewData,
  ReportDashboardMetricOptions,
  ReportsBarChartLabelOptions,
  ReportsBarChartOptions,
  ReportsPieChartOptions,
  VisualizationMapperOperator,
  Blueprint,
} from '@core/reports-types';
import { datasetValueFormatterOptions } from '@core/reports-util';
import type { DurationPipe } from '@core/user-interface-legacy';
import { valueFormatter, pipeCompose } from '@core/utils-legacy';

import { pivotTable, summary, table } from './generic';
import { breakdownTooltipContextFactory } from './strategies/breakdown/tooltip';
import { BAR_CHART_BAR_MAX_WIDTH } from './strategies/chart-layout';
import { rateValueXAxis } from './strategies/composables/rate-axis';
import { buildSignSplitBars, pickSignSplitParam } from './strategies/composables/sign-split';
import {
  addYAxisCategoryTruncate,
  createYAxisCategoryFactory,
} from './strategies/config-factories/y-axis';
import { dataZoom } from './strategies/datazoom';
import { categoryNameFormatter } from './strategies/formatter';
import { getXAxisNameLabel } from './strategies/get-x-axis-name-label';
import { filterByMetricKind, fromBlueprints } from './strategies/operators';
import { filterDatasetForPieChart } from './strategies/pie-dataset';
import { singleMetricTooltipContextFactory } from './strategies/single-metric/tooltip';
import { xAxisMediaQueries } from './strategies/x-axis-media-queries';

// eslint-disable-next-line max-lines-per-function
const barChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  reportsTranslations: ReportsTranslations,
  durationPipe: DurationPipe,
): ReportsBarChartOptions<'value', 'category'> => {
  const dataZoomCmp = dataZoom(component, dataset.getRawData());
  const stack = component.primaryGroup || undefined;
  const isRateSingleSeries = metric.unitType === 'rate' && dataset.isSingleDimension;

  const labelSettings: {
    label: ReportsBarChartLabelOptions;
  } = {
    label: {
      show: dataset.isSingleDimension,
      position: 'right',
      formatter: params => {
        const data = Array.isArray(params.data) ? params.data : [params.data];
        const value = <number>data[params.seriesIndex! + 1];

        return value > 0 || metric.unitType === 'rate'
          ? valueFormatter(value, datasetValueFormatterOptions(dataset), reportsTranslations.i18n)
          : '';
      },
      rich: {},
    },
  };

  const isBreakdownView =
    !!component.secondaryGroup || (!dataset.isSingleDimension && !!metric.defaultGroup);

  const signSplit = isRateSingleSeries
    ? buildSignSplitBars(dataset, reportsTranslations.i18n)
    : null;

  const series: BarSeriesOption[] = signSplit
    ? signSplit.series
    : dataset.dimensions.slice(1).map((dimension, index) => ({
        stack,
        id: dimension.id || dimension.name,
        name: dimension.name,
        type: 'bar',
        seriesLayoutBy: 'column',
        cursor: 'default',
        ...labelSettings,
        emphasis: {
          ...labelSettings,
        },
        barMaxWidth: BAR_CHART_BAR_MAX_WIDTH,
      }));

  if (!dataset.isSingleDimension && !isBreakdownView) {
    dataset.calcTotals().forEach(([categoryName, value]) => {
      series.push({
        legendHoverLink: false,
        z: 3,
        type: 'bar',
        label: {
          fontSize: 11,
          fontWeight: 600,
          position: 'right',
          show: true,
          formatter: () =>
            valueFormatter(value, datasetValueFormatterOptions(dataset), reportsTranslations.i18n),
          silent: true,
          rich: {},
        },
        data: [[0, categoryName]],
      });
    });
  }

  const xAxis =
    metric.unitType === 'rate'
      ? rateValueXAxis(dataset, reportsTranslations.i18n)
      : {
          minInterval: 1,
          axisLabel: { show: false },
          axisTick: { show: false },
          boundaryGap: <[number, string]>[0, '5%'],
          type: 'value',
        };

  const yAxis = pipeCompose(createYAxisCategoryFactory(dataset), addYAxisCategoryTruncate);

  return <ReportsBarChartOptions<'value', 'category'>>{
    dataZoom: dataZoomCmp,
    graphic: [getXAxisNameLabel(metric.xAxis)],
    grid: {
      right: 90,
    },
    ...(signSplit ? { legend: { show: false } } : {}),
    yAxis,
    xAxis,
    tooltip: {
      show: true,
      trigger: 'axis',
      axisPointer: {
        type: 'none',
      },
      // TODO shared analytics - single metric tooltip didn't support double grouping, but it should also be names differently
      // ie. breakdtown tooltip is more versatile and isn't just a berakdown tooltip
      formatter: params =>
        !isBreakdownView
          ? singleMetricTooltipContextFactory(
              dataset,
              reportsTranslations,
              signSplit ? <any>pickSignSplitParam(<any>params) : params,
            )
          : <any>breakdownTooltipContextFactory(dataset, reportsTranslations, <any>params, false),
    },
    dataset: signSplit ? signSplit.dataset : dataset.dataset,
    series,
    media: [...xAxisMediaQueries(metric)],
  };
};

const pieChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  translations: ReportsTranslations,
  durationPipe: DurationPipe,
): ReportsPieChartOptions => ({
  dataset: dataset.dataset,
  legend: {
    formatter: categoryNameFormatter(dataset),
  },
  tooltip: {
    show: true,
    trigger: 'item',
    axisPointer: {
      type: 'none',
    },
    formatter: params => singleMetricTooltipContextFactory(dataset, translations, params),
  },
  series: [
    {
      type: 'pie',
      label: {
        show: dataset.isSingleDimension,
        position: 'outside',
        formatter: (params: CallbackDataParams) => {
          const name = dataset.getCategoryNameById(params.name);
          const data = Array.isArray(params.data) ? params.data : [params.data];
          const value = valueFormatter(
            <string | number>data[params.seriesIndex! + 1],
            datasetValueFormatterOptions(dataset),
            translations.i18n,
          );
          return `${name}\n{pct|${value}}`;
        },
      },
    },
  ],
});

export const singleMetricBlueprints = (
  sanitizer: DomSanitizer,
  translations: ReportsTranslations,
  durationPipe: DurationPipe,
): Array<Blueprint<any>> => [
  table(translations.i18n, ReportDashboardComponentKind.SingleMetric),
  pivotTable(translations, ReportDashboardComponentKind.SingleMetric),
  summary(translations.i18n, ReportDashboardComponentKind.SingleMetric),
  {
    match: {
      kind: ReportDashboardComponentKind.SingleMetric,
      viewMode: ReportDashboardComponentViewMode.Chart,
      chartType: ReportDashboardComponentChartType.Bar,
    },
    factory: ({ component, dataset: originalDataset, metric, meta }) => {
      let dataset = originalDataset;
      if (meta.truncateDataset) {
        const truncateLength =
          typeof meta.truncateDataset === 'number'
            ? meta.truncateDataset
            : meta.truncateDataset(component, dataset);
        dataset = dataset.truncateDataset('first', truncateLength);
      }
      const vis = barChart(component, dataset, metric, translations, durationPipe);
      return {
        vis,
        dataset,
      };
    },
  },
  {
    match: {
      kind: ReportDashboardComponentKind.SingleMetric,
      viewMode: ReportDashboardComponentViewMode.Chart,
      chartType: ReportDashboardComponentChartType.Pie,
    },
    factory: ({ component, dataset, metric }) => {
      const filteredDataset = filterDatasetForPieChart(dataset);
      return {
        vis: pieChart(component, filteredDataset, metric, translations, durationPipe),
        dataset: filteredDataset,
      };
    },
  },
];

export const singleMetric = (
  sanitizer: DomSanitizer,
  translations: ReportsTranslations,
  durationPipe: DurationPipe,
): VisualizationMapperOperator =>
  pipe(
    filterByMetricKind(ReportDashboardComponentKind.SingleMetric),
    fromBlueprints(singleMetricBlueprints(sanitizer, translations, durationPipe)),
  );
